import type { ProjectState } from "@minga/core"
import { useEffect, useRef } from "react"

/**
 * What the WebMCP page tools (decision 0123) read from the page beyond the URL: the open project's state (to turn
 * refs like R-449 and P-8 into the ids the URL holds, and back) and the open chat (whose chat, the composer's draft,
 * the send the composer uses). The project route and the chat view publish here; the page tools read. Module state:
 * one tab has one page.
 */

export interface ProjectBridge {
  readonly workspaceId: string
  readonly projectId: string
  readonly name: string | null
  readonly slug: string | null
  readonly state: ProjectState
}

export interface ChatBridge {
  readonly workspaceId: string
  readonly projectId: string
  readonly agentId: string
  readonly agentName: string
  /** The composer's text, not sent. */
  readonly draft: () => string
  /** Post a message into this chat, as the composer's Send does. */
  readonly send: (text: string) => void
}

let project: ProjectBridge | null = null
let chat: ChatBridge | null = null
const listeners = new Set<() => void>()
const notify = () => {
  for (const l of [...listeners]) l()
}

export const projectBridge = (): ProjectBridge | null => project
export const chatBridge = (): ChatBridge | null => chat

export const setProjectBridge = (next: ProjectBridge | null): void => {
  project = next
  notify()
}
export const setChatBridge = (next: ChatBridge | null): void => {
  chat = next
  notify()
}
export const subscribeBridge = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The bridge of `projectId` once its state is in (after a navigation), or null after `timeoutMs`. */
export const waitForProject = (projectId: string, timeoutMs: number): Promise<ProjectBridge | null> => {
  if (project?.projectId === projectId) return Promise.resolve(project)
  return new Promise((resolve) => {
    const done = (value: ProjectBridge | null) => {
      clearTimeout(timer)
      off()
      resolve(value)
    }
    const off = subscribeBridge(() => {
      if (project?.projectId === projectId) done(project)
    })
    const timer = setTimeout(() => done(null), timeoutMs)
  })
}

/** The project route publishes its state (null until the snapshot is in); cleared when the route unmounts. */
export const useWebMcpProject = (
  workspaceId: string,
  projectId: string,
  listed: { readonly name: string; readonly slug: string } | null | undefined,
  state: ProjectBridge["state"] | null | undefined,
): void => {
  const name = listed?.name ?? null
  const slug = listed?.slug ?? null
  useEffect(() => {
    if (!state) return
    setProjectBridge({ workspaceId, projectId, name, slug, state })
  }, [workspaceId, projectId, name, slug, state])
  useEffect(
    () => () => {
      if (project?.projectId === projectId) setProjectBridge(null)
    },
    [projectId],
  )
}

/** The open chat publishes whose chat it is, its draft and its send; cleared when the chat unmounts. */
export const useWebMcpChat = (
  at: { readonly workspaceId: string; readonly projectId: string },
  agent: { readonly id: string; readonly name: string },
  draft: string,
  send: (text: string) => void,
): void => {
  const draftRef = useRef(draft)
  draftRef.current = draft
  const sendRef = useRef(send)
  sendRef.current = send
  const { workspaceId, projectId } = at
  useEffect(() => {
    const mine: ChatBridge = {
      workspaceId,
      projectId,
      agentId: agent.id,
      agentName: agent.name,
      draft: () => draftRef.current,
      send: (text) => sendRef.current(text),
    }
    setChatBridge(mine)
    return () => {
      if (chat === mine) setChatBridge(null)
    }
  }, [workspaceId, projectId, agent.id, agent.name])
}
